/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MoreVertical, Forward, Link2, Code, Scissors } from 'lucide-mui';
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import ActionButton from '../../plan/upgrade/ActionButton';
import {
  downloadFile,
  setTrimmerVisibility,
  setClipperVisibility,
  shareThunk,
} from '../selectedVideoSlice';
import { RootState } from '../../../reducers';
import { pushModal } from '../../globalModals/globalModalsSlice';
import { requestStatusType } from '../../../types';
import useLimits from '../../plan/hooks/limits';
import { VIDEO_ACTIONS, ActionType } from './actions';
import EmbedModal from './modals/Embed';

const FileActions = () => {
  const guardLimits = useLimits();
  const dispatch = useDispatch();
  const { selectedVideo } = useSelector((state: RootState) => state);
  const { actions: actionsState, file } = selectedVideo;
  const { _id: fileId, defaultProfile } = file || {};

  const containerRef = useRef<HTMLDivElement>(null);
  const shareButtonRef = useRef<HTMLButtonElement>(null);
  const actionsButtonRef = useRef<HTMLButtonElement>(null);

  const [shareAnchorEl, setShareAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [embedModalOpen, setEmbedModalOpen] = useState(false);

  const handleShareClick = (event: React.MouseEvent<HTMLElement>) => {
    setShareAnchorEl(event.currentTarget);
  };

  const handleShareClose = () => {
    setShareAnchorEl(null);
  };

  const handleActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };

  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };

  const handleAction = (key: ActionType) => {
    handleShareClose();
    handleActionsClose();
    switch (key) {
      case 'share':
        dispatch(shareThunk({ fileId }));
        break;
      case 'clip':
        dispatch(setClipperVisibility(true));
        break;
      case 'download':
        guardLimits('download', () => {
          if (defaultProfile) {
            dispatch(downloadFile({ fileId, profile: defaultProfile }));
          }
        });
        break;
      case 'move':
        dispatch(pushModal({ type: 'move-file-modal', files: [file] }));
        break;
      case 'trim':
        dispatch(setTrimmerVisibility(true));
        break;
      case 'delete':
        dispatch(pushModal({ type: 'delete-files-modal', files: [file] }));
        break;
      case 'embed':
        setEmbedModalOpen(true);
        break;
      default:
    }
  };

  const downloadAction = VIDEO_ACTIONS.find(
    (action) => action.actionType === 'download'
  );
  const status = downloadAction
    ? actionsState[downloadAction.actionType]?.status || 'idle'
    : 'idle';

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      ref={containerRef}
      sx={{
        '& .MuiButton-root': {
          height: '36px', // Standard MUI button height
        },
      }}
    >
      <Button
        ref={shareButtonRef}
        variant="contained"
        color="primary"
        onClick={handleShareClick}
        startIcon={<Forward sx={{ width: 16, height: 16 }} />}
        sx={{
          transition: 'all 0.2s ease-in-out',
          px: 2,
        }}
      >
        Share
      </Button>
      <Menu
        anchorEl={shareAnchorEl}
        open={Boolean(shareAnchorEl)}
        onClose={handleShareClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            minWidth: '160px',
            marginTop: '4px',
          },
        }}
      >
        <MenuItem
          onClick={() => handleAction('share')}
          sx={{
            py: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon>
            <Link2 sx={{ width: 16, height: 16 }} />
          </ListItemIcon>
          <ListItemText primary="Copy Link" sx={{ ml: -1 }} />
        </MenuItem>
        <MenuItem
          onClick={() => handleAction('clip')}
          sx={{
            py: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon>
            <Scissors sx={{ width: 16, height: 16 }} />
          </ListItemIcon>
          <ListItemText primary="Share Clip" sx={{ ml: -1 }} />
        </MenuItem>
        <MenuItem
          onClick={() => handleAction('embed')}
          sx={{
            py: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <ListItemIcon>
            <Code sx={{ width: 16, height: 16 }} />
          </ListItemIcon>
          <ListItemText primary="Embed" sx={{ ml: -1 }} />
        </MenuItem>
      </Menu>

      {downloadAction && (
        <ActionButton
          data-testid="action-button-download"
          tooltipMessage={
            downloadAction.tooltipMessages[status as requestStatusType]
          }
          flashTooltip={status === 'succeeded' || status === 'failed'}
          startIcon={
            <downloadAction.IconComponent sx={{ width: 16, height: 16 }} />
          }
          loading={status === 'pending'}
          onClick={() => handleAction('download')}
          variant="outlined"
          color="secondary"
          sx={{
            minWidth: '120px',
            transition: 'all 0.2s ease-in-out',
            '& .MuiButton-label': {
              transition: 'width 0.2s ease-in-out',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            },
          }}
        >
          {downloadAction.buttonTexts[status as requestStatusType]}
        </ActionButton>
      )}

      <Button
        ref={actionsButtonRef}
        onClick={handleActionsClick}
        variant="outlined"
        color="secondary"
        sx={{
          minWidth: 0,
          p: 1,
          transition: 'all 0.2s ease-in-out',
        }}
      >
        <MoreVertical sx={{ width: 16, height: 16 }} />
      </Button>
      <Menu
        anchorEl={actionsAnchorEl}
        open={Boolean(actionsAnchorEl)}
        onClose={handleActionsClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: '160px',
            marginTop: '4px',
          },
        }}
      >
        {VIDEO_ACTIONS.filter((action) =>
          ['trim', 'move', 'delete'].includes(action.actionType)
        ).map(({ actionType, buttonTexts: { idle }, IconComponent }) => (
          <MenuItem
            key={actionType}
            onClick={() => handleAction(actionType)}
            sx={{
              py: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemIcon>
              <IconComponent sx={{ width: 16, height: 16 }} />
            </ListItemIcon>
            <ListItemText primary={idle} sx={{ ml: -1 }} />
          </MenuItem>
        ))}
      </Menu>
      {embedModalOpen && (
        <EmbedModal onClose={() => setEmbedModalOpen(false)} />
      )}
    </Box>
  );
};

export default FileActions;
